import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Filter as FilterIcon, Info as InfoIcon, Plus as PlusIcon } from 'lucide-react';
import { paths } from 'configuration/paths';
import { TranslationsKeys } from 'types/translation';
import { CustomerVisit } from 'types/customer';
import { useTranslate } from 'hooks/useTranslate';
import { usePagination } from 'hooks/usePagination';
import { SearchInput } from 'components/ui/SearchInput';
import { Spinner } from 'components/Spinner';
import { Pagination } from 'components/Pagination';
import { Tooltip } from 'components/ui/Tooltip';
import { dateIsAfter, dateIsEqual, dateIsThisMonth, dateIsThisWeek, dateIsTomorrow } from 'utils/date';
import { Visit } from './Visit';
import { FilterValues, PlannedVisitFilters } from './PlannedVisitsFilters';
import styles from '../MySurveys.module.scss';

export interface IProps {
  visits: CustomerVisit[];
  loading: boolean;
}

export const PlannedVisits = ({ visits, loading }: IProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [visitQuery, setVisitQuery] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [filter, setFilter] = useState(FilterValues.UPCOMING);
  
  const visibleVisits = useMemo(() => {
    let result = visits;
    switch (filter) {
      case FilterValues.UPCOMING:
        result = result.filter(v => dateIsAfter(new Date(v.plannedVisit.startDate), new Date()));
        break;
      case FilterValues.OPEN:
        // =all visits
        break;
      case FilterValues.TODAY:
        result = result.filter(v => dateIsEqual(new Date(v.plannedVisit.startDate), new Date()));
        break;
      case FilterValues.TOMORROW:
        result = result.filter(v => dateIsTomorrow(new Date(v.plannedVisit.startDate)));
        break;
      case FilterValues.WEEK:
        result = result.filter(v => dateIsThisWeek(new Date(v.plannedVisit.startDate)));
        break;
      case FilterValues.MONTH:
        result = result.filter(v => dateIsThisMonth(new Date(v.plannedVisit.startDate)));
        break;
    }

    if (visitQuery) {
      result = result.filter((visit) => {
        return (
          visit.customer.companyName.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.customer.contactName.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.customer.address.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.customer.city.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.customer.country.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.customer.state?.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.customer.sector.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.customer.businessAreaCode?.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.customer.customerId.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.plannedVisit.startDate.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.plannedVisit.visitTypeCode.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.plannedVisit.owner.name?.toLowerCase().includes(visitQuery.toLowerCase()) ||
          visit.plannedVisit.attendees.some((attendee) =>
            attendee.name?.toLowerCase().includes(visitQuery.toLowerCase())
          )
        );
      });
    }

    return result;
  }, [visits, filter, visitQuery]);
  
  const [page, setPage] = useState(1);
  const { items: paginatedVisits, pages } = usePagination(visibleVisits, page);

  return (
    <div>
      <h1 className={styles.mySurveysHeading}>
        <span>
          {t(TranslationsKeys.mySurveysPage_myVisits_title)} {!loading && visits && <>({visits.length})</>}
        </span>
        <span className={styles.mySurveysInfo} onClick={() => setShowTooltip(!showTooltip)}><InfoIcon /></span>
        <Tooltip tip="middle"
                 show={showTooltip}
                 onClick={() => setShowTooltip(false)}>
          <b>{t(TranslationsKeys.mySurveysPage_myVisits_title)}</b><br/>
          {t(TranslationsKeys.mySurveysPage_info_plannedVisits)}
        </Tooltip>
      </h1>
      <div className={styles.mySurveysSearchForm}>
        <SearchInput
          variant="inline"
          onChange={(query) => {
            setVisitQuery(query);
            setPage(1);
          }}
        />
        <div className={styles.mySurveysSearchFormInfoActions}>
          <div className={styles.mySurveysSearchFormResultCount}>
            {visits.length === visibleVisits.length
              ? t(TranslationsKeys.common_all)
              : `${visibleVisits.length} ${t(TranslationsKeys.common_results).toLowerCase()}`}
          </div>
          <button className={styles.mySurveysSearchFormFilter} onClick={() => setShowFilters(true)}>
            <FilterIcon />
            {filter === FilterValues.OPEN
              ? t(TranslationsKeys.common_filters)
              : t(TranslationsKeys.mySurveysPage_search_filtersToggle, { activeFiltersCount: `1` })}
          </button>
        </div>
      </div>
      <button className={styles.mySurveysStartNewSurvey} onClick={() => navigate(paths.surveyIntro)}>
        <PlusIcon />
        {t(TranslationsKeys.mySurveysPage_newSurveyWithoutCustomer)}
      </button>
      {loading && (
        <div className={styles.mySurveysSpinner}>
          <Spinner size="large" />
        </div>
      )}
      {!loading &&
        paginatedVisits?.length > 0 &&
        paginatedVisits.map((visit) => <Visit
          key={visit.plannedVisit.visitId}
          visit={visit}
          visitQuery={visitQuery}
          buttonLabel={t(TranslationsKeys.common_select)}
          buttonClass="primary"
          onSelect={() => navigate(paths.surveyIntroFromVisit.replace(':visitId', visit.plannedVisit.visitId))}
        />)}
      {!loading && <Pagination page={page} pages={pages} onSelectPage={setPage} />}
      {showFilters && 
        <PlannedVisitFilters
          filter={filter}
          onFilterChange={(filter) => {
            setFilter(filter);
            setPage(1);
            setShowFilters(false);
          }}
          onClose={() => setShowFilters(false)}
      />}
    </div>
  );
};
